import { css, cx } from '@linaria/core';
import { PlatformResourceVersionSelector, PlatformResourceVersionSelectorProps } from 'components/PlatformResourceVersionSelector/PlatformResourceVersionSelector';
import { usePlatformResourceVersionSelector } from 'components/PlatformResourceVersionSelector/usePlatformResourceVersionSelector';
import { useFormikContext } from 'formik';
import { FormComponentRenderer } from 'modules/Models/components/ModelForm/components/FormComponentRenderer';
import { dataTypeToFieldTypeMap } from 'modules/Models/components/ModelForm/utils/dataTypeToFieldTypeMap';
import { splitFieldsByUIPriority } from 'modules/Models/components/ModelForm/utils/utils';
import { useListModelTypes } from 'modules/Playground/api/hooks/useListModelTypes';
import { getFieldValueMap } from 'modules/Playground/utils';
import { useListModelVersions } from 'modules/WorkflowV2/api/hooks/useListModelVersions';
import { FC } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useDeepCompareEffect } from 'react-use';
import { breakpoints } from 'styles/utils';
import { useFeatureFlagsLD } from 'modules/FeatureFlagsLD/FeatureFlagsProviderLD';
import { getModelSignaturesFromModelVersion } from '../hooks/utils/useFetchModelOutput/getModelSignaturesFromModelVersion';
import { PlaygroundConfig } from '../types/playgroundConfig';
import { useCompareMode } from '../hooks/utils/useCompareMode';
import { getModelUniqueId } from '../utils/getModelUniqueId';
interface PlaygroundChatConfigSidebarProps {
  className?: string;
}
export const PlaygroundChatConfigSidebar: FC<PlaygroundChatConfigSidebarProps> = ({
  className
}) => {
  const {
    values,
    setFieldValue
  } = useFormikContext<PlaygroundConfig>();
  const {
    model,
    modelVersion
  } = values;
  const {
    isCompareMode
  } = useCompareMode();
  const {
    model: selectedModel,
    legacyParamSpecs
  } = values;
  const {
    openAICompatibleModelsLDFF
  } = useFeatureFlagsLD();
  const openAICompatibleModelList = openAICompatibleModelsLDFF?.modelList ?? [];
  const modelUniqueId = getModelUniqueId(values.model);
  const isOpenAICompatibleModel = openAICompatibleModelList.includes(modelUniqueId);
  const {
    data: modelTypeFields
  } = useListModelTypes({
    isEnabled: !!selectedModel?.id
  });
  const modelParams: CF.API.Models.Params = model?.model_version?.output_info?.params || {};
  const methodSignatures = getModelSignaturesFromModelVersion(model?.model_version);
  const modelParamSpecs = values.isStreaming ? methodSignatures?.find(each => each.name === 'generate')?.input_fields?.filter(eachField => (eachField.is_param as boolean)) ?? [] : methodSignatures?.find(each => each.name === 'predict')?.input_fields?.filter(eachField => (eachField.is_param as boolean)) ?? [];
  const excludedParams = ['api_key']; // TODO: Why do we need this?

  useDeepCompareEffect(() => {
    if (modelParamSpecs.length) {
      let fieldValueMap = {};
      // This sets up the param specs in the fieldValueMap
      modelParamSpecs.forEach(({
        name,
        default: defaultValue
      }) => {
        if (name && !excludedParams.some(excluded => name.includes(excluded))) {
          const key = (name.split('.').pop() as keyof CF.API.Models.Params);
          let value = defaultValue ?? modelParams?.[key];
          // Tech DEBT: temporary fix for string values that are wrapped in quotes
          // Remove this once the backend is fixed to not return strings wrapped in quotes
          // Ticket: https://clarifai.atlassian.net/browse/EAGLE-6255
          if (typeof value === 'string' && value.startsWith('"') && value.endsWith('"')) {
            value = value.slice(1, -1); // Remove quotes if present
          }
          fieldValueMap = {
            ...fieldValueMap,
            [name]: value
          };
        }
      });
      setFieldValue('basicFields', modelParamSpecs.map(each => ({
        ...each,
        path: each.name
      })));
      setFieldValue('modelParamSpecs', modelParamSpecs);
      setFieldValue('fieldValueMap', fieldValueMap);
      // HACK: This is a temporary fix to suppress parameters for OpenAI compatible models /w no method signatures
    } else if (selectedModel && modelTypeFields && !isOpenAICompatibleModel) {
      const [fieldValueMap, trainingTemplateFields, legacyParamSpecsVal] = getFieldValueMap(((selectedModel as unknown) as CF.API.Models.Model), (modelTypeFields.model_types as CF.API.Models.ModelTypesEntity[]));
      const [basicFields, advancedFields] = splitFieldsByUIPriority(legacyParamSpecsVal);
      setFieldValue('basicFields', basicFields);
      setFieldValue('advancedFields', advancedFields);
      setFieldValue('fieldValueMap', fieldValueMap);
      setFieldValue('trainingTemplateFields', trainingTemplateFields);
      setFieldValue('legacyParamSpecs', legacyParamSpecsVal);
    }
  }, [modelParamSpecs, selectedModel?.id, modelTypeFields]);
  const {
    data: modelVersionsListResponse
  } = useListModelVersions({
    params: {
      userAppIdAppId: model?.app_id ?? '',
      userAppIdUserId: model?.user_id ?? '',
      modelId: model?.id ?? ''
    },
    query: {}
  });
  const {
    type,
    options,
    loading,
    selected
  } = usePlatformResourceVersionSelector({
    type: 'models',
    // TODO: Platform Resource Version selector does not support new API Types Yet
    data: ((modelVersionsListResponse?.model_versions as unknown) as CF.API.Models.ModelVersion[]) ?? [],
    loading: false,
    selected: modelVersion?.id
  });
  const handleVersionChange: PlatformResourceVersionSelectorProps['onChange'] = option => {
    const modelVersionObject = modelVersionsListResponse?.model_versions?.find(version => version.id === option?.value);
    setFieldValue('modelVersion', modelVersionObject);
  };
  if (
  // New model param specs is not available
  !modelParamSpecs.length &&
  // Model has methodSignatures but param specs are not available
  !(!methodSignatures && legacyParamSpecs?.length))
    // Do not display the sidebar
    return null;
  return <div className={cx(playgroundContentSidebar, isCompareMode && compareModeStyle, className)}>
      <div className={modelVersionField}>
        <span className={labelText}>Model Version:</span>
        <p className={descriptionText}>Select the target version of this model to use.</p>
        <PlatformResourceVersionSelector className={playgroundVersionSelector} copyIdEnabled isLoading={loading} onChange={handleVersionChange} options={options} type={type} value={selected} />
      </div>
      <ErrorBoundary fallback={<div className={parametersField}>
            <span className={labelText}>Model Parameters:</span>
            <p>Failed to load parameters.</p>
          </div>}>
        {/* Render new form fields based on modelParamSpecs derived from methodSignatures */}
        {modelParamSpecs.length ? <div className={parametersField}>
            <span className={labelText}>Parameters:</span>
            <div className={paramFormContainer}>
              {modelParamSpecs.map(field => {
            const {
              name,
              type: dataType
            } = field;
            if (field && name && dataType) {
              const fieldType = dataTypeToFieldTypeMap[((dataType as unknown) as number)];
              return (
                // TODO: Find out why types are not matching "as unknown as ..."
                <FormComponentRenderer key={`${name}-${fieldType}`} path={name} fieldType={fieldType} field={((field as unknown) as CF.API.Models.ModelTypeFieldsEntity)} />
              );
            } else {
              return null;
            }
          })}
            </div>
          </div> :
      // If no modelParamSpecs, and methodSignatures is undefined render legacyParamSpecs
      !methodSignatures && legacyParamSpecs?.length ? <div className={parametersField}>
            <span className={labelText}>Parameters:</span>
            <div className={paramFormContainer}>
              {legacyParamSpecs.map(field => {
            return <FormComponentRenderer key={field.path} path={field.path} fieldType={field.field_type} field={field} />;
          })}
            </div>
          </div> : null}
      </ErrorBoundary>
    </div>;
};
const playgroundContentSidebar = "odules__Playground__Components__PlaygroundChatConfigSidebar_tsx-playgroundContentSidebar-p1q2vwfi";
export const playgroundSidebarHeader = "odules__Playground__Components__PlaygroundChatConfigSidebar_tsx-playgroundSidebarHeader-p1qkwecb";
const labelText = "odules__Playground__Components__PlaygroundChatConfigSidebar_tsx-labelText-lol2sog";
const descriptionText = "odules__Playground__Components__PlaygroundChatConfigSidebar_tsx-descriptionText-d1r3pdbb";
const playgroundVersionSelector = "odules__Playground__Components__PlaygroundChatConfigSidebar_tsx-playgroundVersionSelector-p1hsjqmi";
const modelVersionField = "odules__Playground__Components__PlaygroundChatConfigSidebar_tsx-modelVersionField-m23r98l";
const parametersField = "odules__Playground__Components__PlaygroundChatConfigSidebar_tsx-parametersField-p12rilhr";
const paramFormContainer = "odules__Playground__Components__PlaygroundChatConfigSidebar_tsx-paramFormContainer-p1h30ev2";
const compareModeStyle = "odules__Playground__Components__PlaygroundChatConfigSidebar_tsx-compareModeStyle-c1it47gk";

require("./PlaygroundChatConfigSidebar.linaria.module.css!=!../../../node_modules/@linaria/webpack-loader/node_modules/@linaria/webpack5-loader/lib/outputCssLoader.js!./PlaygroundChatConfigSidebar.tsx");